/**
 * The few Payments API (V3, D-29) checks the payment module runs on every answer. Kept apart from
 * `paymentsApi.ts`, which holds the V3 flow and is only imported on a V3 payment.
 */

/** The pay answer of a Payments API store: the payment the page confirms, and no order yet. */
export interface StartedPayment {
  paymentId: string;
  clientSecret: string;
  amount?: number;
  currency?: string;
  /** Set when the answer names the session it pays. */
  checkoutSessionId?: string;
  /** Set when the page should create the shopper's Stripe Radar session with this key before confirming. */
  radar?: { publishableKey: string };
}

/** A V2 answer (it carries `payment` or `order`) is never read as a started payment. */
export function readStartedPayment(answer: unknown): StartedPayment | null {
  if (!answer || typeof answer !== 'object' || Array.isArray(answer)) return null;
  const { payment, order, paymentId, clientSecret, amount, currency, checkoutSessionId, radar } =
    answer as Record<string, unknown>;
  if (payment !== undefined || order !== undefined) return null;
  if (typeof paymentId !== 'string' || !paymentId || typeof clientSecret !== 'string' || !clientSecret)
    return null;
  return {
    paymentId,
    clientSecret,
    ...(typeof amount === 'number' ? { amount } : {}),
    ...(typeof currency === 'string' ? { currency } : {}),
    ...(typeof checkoutSessionId === 'string' ? { checkoutSessionId } : {}),
    ...(radarKey(radar) ? { radar: { publishableKey: radarKey(radar) as string } } : {}),
  };
}

function radarKey(radar: unknown): string | null {
  const key = radar && typeof radar === 'object' ? (radar as { publishableKey?: unknown }).publishableKey : null;
  return typeof key === 'string' && key.startsWith('pk_') ? key : null;
}

const PENDING_KEY = 'tgd_payments_api_return';
const PENDING_TTL_MS = 60 * 60_000;

/** Before a redirect: the session the payment pays, so the return page can read its secret again. */
export function parkPaymentsApiReturn(paymentId: string, checkoutSessionId: string): void {
  try {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ paymentId, checkoutSessionId, at: Date.now() }));
  } catch {
    // Storage unavailable: the return page polls the payment, as on V2.
  }
}

/** The parked session of `paymentId`, read once; null for any other payment (every V2 payment). */
export function takeParkedPaymentsApiReturn(paymentId: string): { checkoutSessionId: string } | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parked = JSON.parse(raw) as { paymentId?: string; checkoutSessionId?: string; at?: number };
    if (parked.paymentId !== paymentId) return null;
    sessionStorage.removeItem(PENDING_KEY);
    if (!parked.checkoutSessionId || !parked.at || Date.now() - parked.at > PENDING_TTL_MS) return null;
    return { checkoutSessionId: parked.checkoutSessionId };
  } catch {
    return null;
  }
}
